import React from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  CloseOutlined,
  OpenInNewOutlined,
  PlayCircleOutlineOutlined,
} from "@mui/icons-material";
import { TranslateContext } from "../../context/TranslateContext";
import { mediaSrc } from "../../utils/mediaSrc";
import { isShowcaseAudioUrl, isShowcaseImageUrl, isShowcaseVideoUrl } from "../../utils/showcaseMedia";
import ShowcaseMusicArt from "./ShowcaseMusicArt";

function isVideoItem(item) {
  return item?.type === "video" || isShowcaseVideoUrl(item?.media_url);
}

function isAudioItem(item) {
  return (
    item?.category === "music" ||
    item?.type === "audio" ||
    isShowcaseAudioUrl(item?.audio_url)
  );
}

function shareSongToken(url) {
  const m = String(url || "").match(/\/share\/music\/song\/([^/?#]+)/i);
  return m ? m[1] : null;
}

export default function ShowcasePreviewModal({ open, item, onClose, accent }) {
  const { lang } = React.useContext(TranslateContext);
  const [audioTracks, setAudioTracks] = React.useState([]);
  const [audioLoading, setAudioLoading] = React.useState(false);
  const [audioError, setAudioError] = React.useState("");

  const video = isVideoItem(item);
  const audio = isAudioItem(item);

  React.useEffect(() => {
    if (!open || !item) {
      setAudioTracks([]);
      setAudioError("");
      setAudioLoading(false);
      return;
    }

    if (!audio) return;

    const directAudio = item.audio_url || (isShowcaseAudioUrl(item.media_url) ? item.media_url : "");
    if (directAudio) {
      setAudioTracks([{ title: item.title, audio_url: mediaSrc(directAudio) }]);
      setAudioLoading(false);
      return;
    }

    const token = shareSongToken(item.url);
    if (!token) {
      setAudioError(
        lang?.homeShowcaseAudioUnavailable ||
          "Audio preview is not available. Regenerate homepage gallery in Admin → Site Settings.",
      );
      setAudioLoading(false);
      return;
    }

    let cancelled = false;
    setAudioLoading(true);
    setAudioError("");
    (async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL || "";
        const res = await axios.get(
          `${base}/api/music/public/song/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        if (!res.data?.success) {
          setAudioError(
            lang?.homeShowcaseAudioUnavailable ||
              "This track is not shared publicly.",
          );
          setAudioTracks([]);
          return;
        }
        const tracks = Array.isArray(res.data.data?.tracks) ? res.data.data.tracks : [];
        if (!tracks.length) {
          setAudioError(lang?.homeShowcaseAudioUnavailable || "No audio found.");
          return;
        }
        setAudioTracks(
          tracks.map((tr) => ({
            title: tr.title,
            audio_url: mediaSrc(tr.audio_url || ""),
          })),
        );
      } catch {
        if (!cancelled) {
          setAudioError(lang?.homeShowcaseAudioLoadFailed || "Could not load audio.");
        }
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, item, audio, lang]);

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={800} component="span" sx={{ flex: 1 }} noWrap>
          {item.title}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {video && item.media_url && (
          <Box
            component="video"
            src={item.media_url}
            controls
            autoPlay
            playsInline
            sx={{
              width: "100%",
              maxHeight: "70vh",
              borderRadius: 2,
              bgcolor: "#000",
              display: "block",
            }}
          />
        )}

        {audio && !video && (
          <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 360,
                pt: "56.25%",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 4,
              }}
            >
              {isShowcaseImageUrl(item.media_url) ? (
                <Box
                  component="img"
                  src={item.media_url}
                  alt={item.title}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <ShowcaseMusicArt title={item.title} accent={accent} />
              )}
            </Box>
            {audioLoading && <CircularProgress size={36} sx={{ color: accent }} />}
            {audioError && (
              <Typography color="text.secondary" textAlign="center">
                {audioError}
              </Typography>
            )}
            {!audioLoading &&
              !audioError &&
              audioTracks.map((tr, i) => (
                <Box key={tr.audio_url || i} sx={{ width: "100%" }}>
                  {audioTracks.length > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                      {tr.title || `Track ${i + 1}`}
                    </Typography>
                  )}
                  {tr.audio_url ? (
                    <Box
                      component="audio"
                      controls
                      autoPlay={i === 0}
                      src={tr.audio_url}
                      sx={{ width: "100%" }}
                    />
                  ) : null}
                </Box>
              ))}
          </Stack>
        )}

        {!video && !audio && item.media_url && (
          <Box
            component="img"
            src={item.media_url}
            alt={item.title}
            sx={{
              width: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.divider, 0.2),
            }}
          />
        )}

        {!item.media_url && !audioLoading && (
          <Stack alignItems="center" py={4} spacing={1}>
            <PlayCircleOutlineOutlined sx={{ fontSize: 48, color: accent, opacity: 0.5 }} />
            <Typography color="text.secondary">
              {lang?.homeShowcaseNoMedia || "No preview available."}
            </Typography>
          </Stack>
        )}

        {item.url && (
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewOutlined />}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              component="a"
            >
              {lang?.homeShowcaseOpenFull || "Open full page"}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
